import { FunctionNode } from '../node';
import { Control } from '../control';
import { Org } from '../org';
import { Auth } from '../auth';
import { View } from '../view';

/** 权限领域类型 */
export namespace Permission {
    /** 子管理员角色管理范围类型 */
    export enum SubAdministratorRoleScopeType {
        /** 无角色管理范围 */
        None = 0,
        /** 可管理全部角色 */
        All = 1,
        /** 仅可管理指定角色 */
        Partial = 2,
    }

    /** 角色数据权限范围类型 */
    export enum RoleDataScopeType {
        /** 无数据权限 */
        None = -1,
        /** 本人数据 */
        Own = 0,
        /** 本部门数据 */
        OwnDept = 1,
        /** 全部数据 */
        All = 2,
        /** 自定义数据范围 */
        Custom = 3,
    }

    /** 角色列表页权限范围类型 */
    export enum RoleListPageScopeType {
        /** 全部列表页 */
        All = 0,
        /** 自定义列表页 */
        Custom = 1,
    }

    /** 系统默认角色 ID */
    export enum SystemRoleId {
        /** 系统管理员 */
        Admin = '142c7e93-1fad-42be-9317-0b9c1bb94c64',
        /** 子管理员 */
        SubAdmin = '5f23c1bd-abd1-4ba6-91f0-fafc6e8b879c',
        /** 所有用户 */
        AllUser = '1fc8e0be-f7c8-4130-a780-de250e2c8b57',
    }

    /** 用户状态 */
    export enum State {
        /** 未指定 */
        Unspecified = -1,
        /** 已启用 */
        Active = 0,
        /** 已停用 */
        Inactive = 1,
    }

    /** 用户授权状态 */
    export enum AuthorizedState {
        /** 初始状态 */
        InitState = -1,
        /** 未授权 */
        NotAuthorized = 0,
        /** 已授权 */
        Authorized = 1,
        /** 授权已禁用 */
        DisableAuthorized = 2,
        /** 用户授权已禁用 */
        DisableAuthorizedForUser = 3,
    }

    /** 分页请求参数 */
    export interface IPageParams {
        pageIndex: number;
        pageSize: number;
    }

    /** 子管理员信息 */
    export interface ISubAdministratorItem {
        userId: string;
        displayName: string;
        entryId: string;
        departmentId: string;
        departmentName: string;
        departmentEntryId: string;
        canCreateApp: boolean;
        manageAppCodes: string[];
        canManageAcl: boolean;
        canManageRoleAndMember: boolean;
        canCreateRole: boolean;
        roleScopeType: SubAdministratorRoleScopeType;
        manageRoleIds: string[];
        createdTime?: string;
        modifiedTime?: string;
    }

    /** 加载子管理员列表参数 */
    export interface ILoadSubAdministratorsParams extends IPageParams {
        userName?: string;
    }

    /** 加载子管理员列表响应 */
    export interface ILoadSubAdministratorsResponse {
        total: number;
        subAdministrators: ISubAdministratorItem[];
    }

    /** 保存子管理员参数 */
    export interface ISaveSubAdministratorsParams {
        userIds: string[];
        canCreateApp: boolean;
        manageAppCodes?: string[];
        canManageAcl: boolean;
        canManageRoleAndMember: boolean;
        canCreateRole: boolean;
        roleScopeType: SubAdministratorRoleScopeType;
        manageRoleIds?: string[];
    }

    /** 追加子管理员可管理应用参数 */
    export interface IAppendSubAdministratorAppsParams {
        userIds: string[];
        appCodes: string[];
    }

    /** 用户 ID 列表参数 */
    export interface IUserIdsParams {
        userIds: string[];
    }

    /** 角色信息 */
    export interface IRoleItem {
        roleId: string;
        name: string;
        displayName: string;
        sortKey?: number;
        code: string;
        entryId?: string;
        isFromEntry?: boolean;
        groupName?: string;
        groupId?: string;
        isExternal?: boolean;
        type?: Org.UnitType;
        canManage: boolean;
        isSystemRole?: boolean;
    }

    /** 权限管理界面标签 */
    export interface ITagItem {
        id: string;
        label: string;
        color?: Control.StyleRuleColor;
        icon?: string;
        iconColor?: FunctionNode.FunctionNodeIconColorType;
    }

    /** 权限管理界面子管理员 */
    export interface ISubAdminViewItem extends ISubAdministratorItem {
        avatar?: string;
        apps: ITagItem[];
        roles: ITagItem[];
        users?: Org.IOrgInfo[];
    }

    /** 子管理员编辑数据 */
    export interface ISubAdminEditItem extends ISubAdministratorItem {
        users?: Org.IOrgInfo[];
    }

    /** 权限管理界面角色成员 */
    export interface IRoleMemberViewItem extends IRoleMemberItem {
        /** 岗位 ID */
        postId?: string;
        /** 关联用户信息 */
        users?: Org.IOrgInfo[];
        /** 服务部门信息 */
        serviceDepartmentInfos?: Org.IOrgInfo[];
        /** 服务部门名称映射 */
        serviceUnitNames?: Record<string, string>;
    }

    /** 系统按钮权限 */
    export interface ISystemButtonPermission {
        code: View.SystemActionKey;
        name: string;
        enabled: boolean;
        source?: IRoleChildNode;
    }

    /** 自定义按钮权限 */
    export interface ICustomButtonPermission {
        code: string;
        name: string;
        enabled: boolean;
        source?: IRoleChildNode;
    }

    /** 列表页权限 */
    export interface IListPagePermission {
        /** 默认列表页为 null */
        pageId: string | null;
        displayName: string;
        hasAcl: boolean;
    }

    /** 操作权限配置 */
    export interface IOperationPermission {
        systemButtons: ISystemButtonPermission[];
        customButtons: ICustomButtonPermission[];
        sourceAclNode?: IRoleAclNode;
    }

    /** 表单权限配置 */
    export interface ISchemaPermission {
        /** 表单权限是否已加载 */
        loaded?: boolean;
        /** 是否拥有节点访问权限 */
        hasAcl: boolean;
        schemaId: string;
        schemaName: string;
        nodeType: FunctionNode.FunctionNodeType;
        /** 应用功能节点图标名称 */
        icon?: string;
        operationPermission: IOperationPermission;
        scopeType: RoleDataScopeType | null;
        /** 列表页权限范围 */
        viewPageScopeType: RoleListPageScopeType;
        /** 自定义列表页当前勾选的页面 ID */
        viewPageIds: Array<string | null>;
        /** 当前表单下的列表页候选项（仅前端展示） */
        listPagePermissions: IListPagePermission[];
    }

    /** 表单分组权限配置 */
    export interface ISchemaGroup {
        /** 分组权限是否正在加载 */
        loading?: boolean;
        /** 分组权限是否已加载 */
        loaded?: boolean;
        groupId: string;
        groupName: string;
        expanded: boolean;
        schemas: ISchemaPermission[];
    }

    /** 应用权限目录中的根节点：直属表单或表单分组 */
    export type IAppPermissionItem =
        | { type: 'schema'; schema: ISchemaPermission }
        | { type: 'group'; group: ISchemaGroup };

    /** 应用权限配置 */
    export interface IAppPermission {
        appCode: string;
        appName: string;
        /** 当前角色是否在该应用下配置了至少一个权限节点 */
        hasAcl: boolean;
        icon?: string;
        iconColor?: FunctionNode.FunctionNodeIconColorType;
        /** 按应用功能树原始顺序保存的直属表单与表单分组 */
        schemaItems: IAppPermissionItem[];
    }

    /** 未保存权限变更的处理决定 */
    export type ChangeDecision = 'save' | 'discard' | 'cancel';

    /** 切换角色或应用时受影响的未保存权限 */
    export interface IChangeImpact {
        /** 本次切换类型 */
        type: 'role' | 'app';
        /** 当前角色 ID */
        roleId: string;
        /** 存在未保存修改的应用编码 */
        appCodes: string[];
        /** 目标角色 ID */
        targetRoleId?: string;
        /** 目标应用编码 */
        targetAppCode?: string;
    }

    /** 应用层提供的未保存权限确认函数 */
    export type IChangeConfirm = (impact: IChangeImpact) => ChangeDecision | Promise<ChangeDecision>;

    /** 权限管理器创建选项 */
    export interface IManagerOptions {
        /** 未保存权限确认函数 */
        confirmChange?: IChangeConfirm;
    }

    /** 权限编辑会话状态 */
    export interface IEditSession {
        /** 当前会话所属的角色 ID */
        roleId: string;
        /** 当前会话所属的应用编码 */
        appCode: string;
        /** 会话当前的加载、编辑或保存状态 */
        status: 'idle' | 'loading' | 'ready' | 'saving' | 'error';
        /** 最近一次服务端确认的权限快照 */
        origin?: IAppPermission;
        /** 当前界面正在编辑的权限草稿 */
        draft?: IAppPermission;
        /** 已完成权限加载的表单编码 */
        loadedSchemaCodes: Set<string>;
        /** 相对原始快照发生修改的表单编码 */
        dirtySchemaCodes: Set<string>;
        /** 原始权限快照的表单索引，供增量脏状态比较使用 */
        originSchemaMap?: Map<string, ISchemaPermission>;
        /** 草稿表单在权限目录中的位置索引，目录结构稳定时无需重复遍历定位 */
        draftSchemaLocations?: Map<string, { itemIndex: number; schemaIndex?: number }>;
        /** 当前草稿中拥有节点访问权限的表单数量 */
        draftAclSchemaCount?: number;
        /** 草稿编辑版本，每次用户修改后递增 */
        revision: number;
        /** 当前保存请求对应的草稿版本 */
        savingRevision?: number;
        /** 首次加载权限时复用的请求任务 */
        loading?: Promise<IAppPermission>;
        /** 最近一次加载或保存错误 */
        error?: unknown;
    }

    /** 角色或应用切换的处理结果 */
    export enum SelectionStatus {
        /** 已完成切换，当前选中项已更新。 */
        Selected = 'selected',
        /** 本次切换未执行或被取消，当前选中项保持不变。 */
        Cancelled = 'cancelled',
        /** 更晚发起的切换请求已使本次结果失效。 */
        Superseded = 'superseded',
    }

    /** 角色或应用切换的最终结果 */
    export interface ISelectionResult {
        /** 本次角色或应用切换的最终处理结果。 */
        status: SelectionStatus;
    }

    /** 权限角色节点类型 */
    export type RoleKind = 'fixed' | 'group' | 'role';
    /** 权限角色树节点 */
    export interface IRoleNode {
        id: string;
        name: string;
        icon?: string;
        description: string;
        fixed?: boolean;
        kind: RoleKind;
        children?: IRoleNode[];
        code?: string;
        isFromEntry?: boolean;
        isExternal?: boolean;
        isDefault?: boolean;
    }

    /** 权限角色分组选项 */
    export type RoleGroup = Pick<IRoleNode, 'id' | 'name'>;
    /** 权限角色选项 */
    export type RoleOption = Org.IOrgInfo;

    /** 权限管理范围 */
    export interface IManageScope {
        manageableRoleIds: string[];
        manageableAppCodes: string[];
        rolePermissionEnabled: boolean;
        manageRoleAndMember: boolean;
        allowCreateRole: boolean;
        allowCreateGroup: boolean;
        roleScope: SubAdministratorRoleScopeType;
    }

    /** 当前用户权限管理范围 */
    export interface ICurrentUserScope extends IManageScope {
        authType: Auth.UserAuthType;
    }

    /** 权限上下文初始化数据 */
    export interface IBootstrap {
        authType: Auth.UserAuthType;
        fixedRoles: IRoleNode[];
        customRoleTree: IRoleNode[];
        apps: IAppPermission[];
        manageScope: IManageScope;
    }

    /** 加载分组角色结果 */
    export interface IFetchGroupRolesResult {
        groupId: string;
        roles: IRoleNode[];
    }

    /** 权限角色树状态 */
    export interface IRoleTreeState {
        fullRoleTree: IRoleNode[];
        pureGroups: RoleGroup[];
        pureRoles: RoleOption[];
        roleMap: Map<string, IRoleNode>;
        groupMap: Map<string, IRoleNode>;
    }

    /** 创建角色载荷 */
    export interface IRoleCreatePayload {
        name: string;
        groupId: string;
    }

    /** 创建角色分组载荷 */
    export interface IRoleGroupCreatePayload {
        name: string;
    }

    /** 外部角色同步错误信息 */
    export interface IRoleSynchronizationError {
        errorCode: string;
    }

    /** 外部角色同步结果 */
    export interface IRoleSynchronizationResult {
        successful: boolean;
        errorCode?: string;
        errorMessage?: string;
    }

    /** 角色应用权限请求参数 */
    export interface IRoleAppPermissionParams {
        roleId: string;
        appCode: string;
    }

    /** 加载角色应用摘要参数 */
    export interface ILoadRoleAppsParams {
        roleId: string;
    }

    /** 权限管理器初始化选项 */
    export interface IManagerInitOptions {
        scopeAppCode?: string;
        apps?: IAppPermission[];
        /** 已加载的应用功能节点 */
        appFunctionNodes?: FunctionNode.IFunctionNode[];
    }

    /** 角色分组信息 */
    export interface IRoleGroupItem {
        groupId: string;
        name: string;
        sortKey: number;
        code: string;
        isFromEntry: boolean;
        isExternal: boolean;
        isDefault: boolean;
        entryId: string;
    }

    /** 创建角色分组参数 */
    export interface ICreateRoleGroupParams {
        groupName: string;
        sortKey?: number;
    }

    /** 创建角色分组响应 */
    export interface ICreateRoleGroupResponse {
        groupId: string;
    }

    /** 删除角色分组参数 */
    export interface IDeleteRoleGroupParams {
        groupId: string;
    }

    /** 删除操作响应 */
    export interface IDeleteResponse {
        result: number;
    }

    /** 更新角色分组参数 */
    export interface IUpdateRoleGroupParams extends IDeleteRoleGroupParams {
        groupName: string;
    }

    /** 角色分组排序参数 */
    export interface ISortRoleGroupsParams {
        groupIds: string[];
    }

    /** 加载角色列表参数 */
    export interface ILoadRolesParams {
        groupId?: string;
        keyword?: string;
    }

    /** 创建角色参数 */
    export interface ICreateRoleParams {
        roleName: string;
        groupId?: string;
        sortKey?: number;
    }

    /** 创建角色响应 */
    export interface ICreateRoleResponse {
        roleId: string;
    }

    /** 删除角色参数 */
    export interface IDeleteRoleParams {
        roleId: string;
    }

    /** 更新角色参数 */
    export interface IUpdateRoleParams extends IDeleteRoleParams {
        roleName: string;
    }

    /** 角色排序参数 */
    export interface ISortRolesParams {
        roleIds: string[];
    }

    /** 加载角色权限子节点参数 */
    export interface ILoadRoleAclChildrenParams {
        roleId: string;
        appCode?: string;

        schemaCodes?: string[];
    }

    /** 加载角色权限节点参数 */
    export interface ILoadRoleAclNodeParams {
        roleId: string;
        code: string;
    }

    /** 角色权限条件项 */
    export interface IRoleAclConditionItem {
        /** 条件 ID */
        id?: string;
        /** 字段编码 */
        propertyName: string;
        /** 比较符 */
        operator: string;
        /** 子比较符 */
        subOperator: string;
        /** 值类型 */
        valueType: string;
        /** 条件值 */
        value: unknown;
        [key: string]: unknown;
    }

    /** 角色权限子节点 */
    export interface IRoleChildNode {
        code: string;
        name: string;
        hasAcl: boolean;
        type: FunctionNode.FunctionNodeType;
        roleAclNode: IRoleAclNode | null;
    }

    /** 角色权限节点 */
    export interface IRoleAclNode {
        /** 操作按钮 */
        actions: IRoleChildNode[];
        /** 是否全选操作按钮 */
        actionsAllChecked: boolean;
        /** 数据范围 */
        scopeType: RoleDataScopeType;
        /** 自定义数据权限 */
        customExpression: IRoleAclConditionItem[][] | null;
        /** 是否仅创建人可见的操作编码 */
        creatorOnlyActionCodes: View.SystemActionKey[] | null;
        /** 列表页权限范围 */
        viewPageScopeType?: RoleListPageScopeType;
        /** 自定义列表页当前勾选的页面 ID */
        viewPageIds?: Array<string | null>;
    }

    /** 保存单个角色权限节点参数 */
    export interface ISaveRoleAclNodeParams extends ILoadRoleAclNodeParams {
        hasAcl: boolean;
        /** 应用级节点只需提交 scopeType */
        aclNode: IRoleAclNode | Pick<IRoleAclNode, 'scopeType'>;
    }

    /** 批量保存角色权限节点参数 */
    export interface ISaveRoleAclNodesParams {
        aclNodes: ISaveRoleAclNodeParams[];
    }

    /** 加载角色成员参数 */
    export interface ILoadRoleMembersParams extends IPageParams {
        roleId: string;
    }

    /** 搜索角色成员参数 */
    export interface ISearchRoleMembersParams extends ILoadRoleMembersParams {
        keyword?: string;
    }

    /** 角色成员信息 */
    export interface IRoleMemberItem {
        /** 用户 ID */
        objectId: string;
        /** 用户名称 */
        name: string;
        /** 用户手机号 */
        mobile: string;
        /** 用户状态 */
        state: State;
        /** 主部门id */
        parentId: string;

        isManager?: boolean;
        /** 主部门名称 */
        parentName: string;
        /** 外部人员所在的根部门名称 */
        externalRootName?: string;
        /** 邮箱 */
        email: string;
        /** 用户头像 URL */
        profilePhotoUrl: string;
        /** 所属部门名称，多个名称以逗号分隔 */
        parentNames: string;
        /** 所属角色名称，多个名称以逗号分隔 */
        roleNames?: string[];
        /** 是否为系统管理员 */
        isAdmin: boolean;
        /** 授权状态 */
        authorizedState: AuthorizedState;
        /** 组织单元类型 */
        type: Org.UnitType;
        /** 第三方平台类型 */
        entryType: string;
        /** 第三方平台用户 ID */
        entryId: string;
        /** 主部门的第三方平台 ID */
        parentEntryId: string;

        [key: string]: unknown;
    }

    /** 加载角色成员响应 */
    export interface ILoadRoleMembersResponse {
        total: number;
        members: IRoleMemberItem[];
    }

    /** 角色成员摘要 */
    export interface IRoleMemberSummaryItem {
        objectId: string;
        name: string;
        type: Org.UnitType;
        entryType: string;
        entryId: string;
    }

    /** 更新角色成员参数 */
    export interface IUpdateRoleMembersParams {
        roleId: string;
        memberIds: string[];
    }

    /** 加载角色成员服务部门参数 */
    export interface ILoadRolePostDepartmentsParams {
        roleId: string;
        userId: string;
    }

    /** 角色成员服务部门信息 */
    export interface IRoleDepartmentItem {
        departmentId: string;
        name: string;
        type: Org.UnitType;
    }

    /** 角色成员服务部门响应 */
    export interface IRolePostDepartmentResponse {
        postId: string;
        departments: IRoleDepartmentItem[];
    }

    /** 更新角色成员服务部门参数 */
    export interface IUpdateRolePostDepartmentsParams extends ILoadRolePostDepartmentsParams {
        postId: string;
        departmentIds: string[];
    }

    /** 复制角色参数 */
    export interface ICopyRoleParams {
        sourceRoleId: string;
        roleName: string;
    }
}
